import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Building2, Users, UserCog, LogOut } from 'lucide-react';

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onMenuClick: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen, onClose, onMenuClick }) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  const menuItems = [
    { path: '/admin/estabelecimentos', label: 'Estabelecimentos', icon: Building2 },
    { path: '/admin/usuarios', label: 'Usuários', icon: Users },
    { path: '/admin/funcionarios', label: 'Funcionários', icon: UserCog },
  ];

  return (
    <>
      {/* Header mobile com hamburger */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-neutral-900 text-white h-16 flex items-center justify-between px-4 z-20 border-b border-neutral-800">
        <button
          onClick={onMenuClick}
          className="text-white p-2 -ml-2"
          aria-label="Abrir menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Gestta" className="h-5 brightness-0 invert" />
          <span className="text-[10px] text-neutral-400 uppercase tracking-wider font-semibold">Admin</span>
        </div>
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      <div
        className={`h-full bg-neutral-900 text-white w-64 fixed left-0 top-0 overflow-y-auto flex flex-col z-40 transform transition-transform duration-300 ease-in-out border-r border-neutral-800 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Botão de fechar para mobile */}
        <button
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 text-white text-3xl hover:text-neutral-300 w-8 h-8 flex items-center justify-center z-50"
          aria-label="Fechar menu"
        >
          ×
        </button>

        <div className="px-6 py-5 border-b border-neutral-800">
          <div className="flex items-center gap-2 pr-8 lg:pr-0">
            <img src="/logo.png" alt="Gestta" className="h-7 brightness-0 invert" />
            <span className="text-[10px] text-neutral-400 uppercase tracking-wider font-semibold">Admin</span>
          </div>
          <p className="text-xs text-neutral-500 mt-2 truncate pr-8 lg:pr-0">{user?.nome}</p>
        </div>

      <nav className="p-4 flex-1">
        <ul className="space-y-3">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => {
                    if (window.innerWidth < 1024) onClose();
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    location.pathname === item.path
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md border-l-4 border-primary-300'
                      : 'text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200 hover:scale-[1.02]'
                  }`}
                >
                  <IconComponent className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

        <div className="p-4 border-t border-neutral-800">
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200 transition-all duration-200 hover:scale-[1.02]"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Sair</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
